import { GL, GLSL_COMMON, Program } from '../gl';
import type { Vec3 } from '../../lib/math';

const VS = /* glsl */ `#version 300 es
precision highp float;
${GLSL_COMMON}
layout(location = 0) in vec2 a_corner;   // x: 0 start / 1 end, y: −1 / +1 side
layout(location = 1) in vec3 a_p0;
layout(location = 2) in vec3 a_p1;
layout(location = 3) in vec2 a_gap;     // gaps at each end, in units of u_gapScale
layout(location = 4) in vec4 a_color;   // per-segment tint (constant when not supplied)
uniform mat3 u_frame;
uniform int u_refract;
uniform float u_width;                  // device pixels
uniform float u_gapScale;
uniform float u_dash;                   // dash period in device pixels, 0 = solid
out float v_side;
out float v_along;
out vec4 v_color;

void main() {
  gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  v_side = 0.0; v_along = 0.0; v_color = vec4(0.0);
  vec3 e0 = u_frame * a_p0;
  vec3 e1 = u_frame * a_p1;
  if (u_refract == 1) { e0 = refractEnu(e0); e1 = refractEnu(e1); }
  vec3 q0 = projectCam(u_enuToCam * e0);
  vec3 q1 = projectCam(u_enuToCam * e1);
  if (q0.z < 0.5 || q1.z < 0.5) return;
  vec2 s0 = (q0.xy * 0.5 + 0.5) * u_viewport;
  vec2 s1 = (q1.xy * 0.5 + 0.5) * u_viewport;
  vec2 d = s1 - s0;
  float L = length(d);
  if (L < 1e-3 || L > 4.0 * max(u_viewport.x, u_viewport.y)) return;
  vec2 dir = d / L;
  vec2 nrm = vec2(-dir.y, dir.x);
  float g0 = a_gap.x * u_gapScale, g1 = a_gap.y * u_gapScale;
  if (g0 + g1 >= L - 1.0) return;
  vec2 a = s0 + dir * g0;
  vec2 b = s1 - dir * g1;
  float hw = u_width * 0.5 + 1.0;
  vec2 pos = mix(a, b, a_corner.x) + nrm * a_corner.y * hw;
  v_side = a_corner.y * hw;
  v_along = mix(0.0, length(b - a), a_corner.x);
  v_color = a_color;
  gl_Position = vec4(pos / u_viewport * 2.0 - 1.0, 0.0, 1.0);
}`;

const FS = /* glsl */ `#version 300 es
precision highp float;
in float v_side;
in float v_along;
in vec4 v_color;
uniform vec4 u_color;
uniform float u_width;
uniform float u_dash;
out vec4 o;
void main() {
  float a = clamp(u_width * 0.5 + 0.5 - abs(v_side), 0.0, 1.0);
  if (u_dash > 0.0) a *= step(fract(v_along / u_dash), 0.55);
  vec4 c = u_color * v_color;
  o = vec4(c.rgb * c.a * a, c.a * a);
}`;

let prog: Program | null = null;

export class LineSet {
  private vao: WebGLVertexArrayObject;
  private posBuf: WebGLBuffer;
  private gapBuf: WebGLBuffer;
  private colBuf: WebGLBuffer;
  private hasGap = false;
  private hasColor = false;
  count = 0;

  constructor(private gl: GL) {
    if (!prog) prog = new Program(gl, VS, FS, 'lines');
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    const quad = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, -1, 1, -1, 0, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.posBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuf);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 24, 0);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 3, gl.FLOAT, false, 24, 12);
    gl.vertexAttribDivisor(2, 1);
    this.gapBuf = gl.createBuffer()!;
    this.colBuf = gl.createBuffer()!;
    gl.bindVertexArray(null);
  }

  /** segments: [x0,y0,z0,x1,y1,z1]*n; gaps: [g0,g1]*n; colors: [r,g,b,a]*n */
  setData(segments: Float32Array, gaps?: Float32Array, colors?: Float32Array, dynamic = false) {
    const gl = this.gl;
    const usage = dynamic ? gl.DYNAMIC_DRAW : gl.STATIC_DRAW;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuf);
    gl.bufferData(gl.ARRAY_BUFFER, segments, usage);
    this.count = segments.length / 6;
    this.hasGap = !!gaps;
    if (gaps) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.gapBuf);
      gl.bufferData(gl.ARRAY_BUFFER, gaps, usage);
      gl.enableVertexAttribArray(3);
      gl.vertexAttribPointer(3, 2, gl.FLOAT, false, 0, 0);
      gl.vertexAttribDivisor(3, 1);
    } else gl.disableVertexAttribArray(3);
    this.hasColor = !!colors;
    if (colors) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.colBuf);
      gl.bufferData(gl.ARRAY_BUFFER, colors, usage);
      gl.enableVertexAttribArray(4);
      gl.vertexAttribPointer(4, 4, gl.FLOAT, false, 0, 0);
      gl.vertexAttribDivisor(4, 1);
    } else gl.disableVertexAttribArray(4);
    gl.bindVertexArray(null);
  }

  draw(setCommon: (p: Program) => void, u: {
    frame: ArrayLike<number>; refract: boolean; width: number; color: [number, number, number, number];
    gapScale?: number; dash?: number; first?: number; count?: number;
  }) {
    if (!this.count) return;
    const gl = this.gl;
    const p = prog!.use();
    setCommon(p);
    p.m3('u_frame', u.frame).i1('u_refract', u.refract ? 1 : 0).f1('u_width', u.width);
    p.f4('u_color', ...u.color).f1('u_gapScale', u.gapScale ?? 0).f1('u_dash', u.dash ?? 0);
    gl.bindVertexArray(this.vao);
    if (!this.hasGap) gl.vertexAttrib2f(3, 0, 0);
    if (!this.hasColor) gl.vertexAttrib4f(4, 1, 1, 1, 1);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    const first = u.first ?? 0;
    const count = u.count ?? this.count - first;
    if (first === 0) gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
    else {
      // Re-point the instance attributes at the requested range.
      gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuf);
      gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 24, first * 24);
      gl.vertexAttribPointer(2, 3, gl.FLOAT, false, 24, first * 24 + 12);
      if (this.hasGap) {
        gl.bindBuffer(gl.ARRAY_BUFFER, this.gapBuf);
        gl.vertexAttribPointer(3, 2, gl.FLOAT, false, 0, first * 8);
      }
      if (this.hasColor) {
        gl.bindBuffer(gl.ARRAY_BUFFER, this.colBuf);
        gl.vertexAttribPointer(4, 4, gl.FLOAT, false, 0, first * 16);
      }
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuf);
      gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 24, 0);
      gl.vertexAttribPointer(2, 3, gl.FLOAT, false, 24, 12);
      if (this.hasGap) {
        gl.bindBuffer(gl.ARRAY_BUFFER, this.gapBuf);
        gl.vertexAttribPointer(3, 2, gl.FLOAT, false, 0, 0);
      }
      if (this.hasColor) {
        gl.bindBuffer(gl.ARRAY_BUFFER, this.colBuf);
        gl.vertexAttribPointer(4, 4, gl.FLOAT, false, 0, 0);
      }
    }
    gl.bindVertexArray(null);
  }
}

/** Great-circle subdivision of one segment (unit vectors) into pieces no longer than maxDeg. */
export function subdivideSegment(a: Vec3, b: Vec3, maxDeg: number, out: number[], gaps?: number[], gapA = 0, gapB = 0) {
  const dotab = Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const ang = Math.acos(dotab);
  const n = Math.max(1, Math.ceil((ang * 180) / Math.PI / maxDeg));
  const sin = Math.sin(ang);
  let prev = a;
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    let p: Vec3 = b;
    if (sin > 1e-9 && i < n) {
      const wa = Math.sin((1 - t) * ang) / sin, wb = Math.sin(t * ang) / sin;
      p = [a[0] * wa + b[0] * wb, a[1] * wa + b[1] * wb, a[2] * wa + b[2] * wb];
    }
    out.push(prev[0], prev[1], prev[2], p[0], p[1], p[2]);
    gaps?.push(i === 1 ? gapA : 0, i === n ? gapB : 0);
    prev = p;
  }
}

/** Subdivide a polyline (no gaps). */
export function subdividePolyline(pts: Vec3[], maxDeg: number, out: number[]) {
  for (let k = 0; k + 1 < pts.length; k++) subdivideSegment(pts[k], pts[k + 1], maxDeg, out);
}
